import {
  useGetAppComment,
  useLoanApplication,
  useLoanApplicationRevision,
  useLoanRequest,
  usePatchAppComment,
  usePatchPerson,
  usePerson,
  useRegisterLoanRequests,
  useUpdateApplicationDetails,
} from '@axo/shared/data-access/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ApplicationDataTabPage,
  ApplicationDataTabType,
  useApplicationDataStore,
} from '../../../store/applicationDataStore';
import { useCallback, useEffect, useMemo } from 'react';
import {
  ApplicationDataFormValues,
  useApplicationDataFormResolver,
} from './ApplicationDataFormSchema';
import {
  ApplicationStatus,
  CarOwnerShipType,
  Debtor,
  event_log,
  ExpenseKind,
  Product,
} from '@axo/shared/data-access/types';
import { ApplicantMode } from '@axo/shared/data-access/types/loan_request';
import { useCreateLoanRequestFromFormValues } from './createLoanRequestFromFormValues';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { EventCodeACE } from '../../EventLogWrapper/event-code';
import { useAxoVersion } from '@axo/shared/data-access/hooks/services/ace';
import {
  useCreateApplicationCar,
  useCreateApplicationExpense,
  useDeleteApplicationCar,
  useDeleteApplicationExpense,
  usePatchApplicationCar,
  usePatchApplicationExpense,
} from '@axo/shared/data-access/hooks/services/loan_application';

type ApplicationDataFormProps = React.PropsWithChildren<{
  applicationID: string;
}>;

export const ApplicationDataForm: React.FC<ApplicationDataFormProps> = ({
  applicationID,
  children,
}) => {
  const appTag: event_log.Resource = {
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  };
  const log = useEventLogger(appTag);
  const { data: latestApplicationData } = useLoanApplication(applicationID);
  const { mutateAsync: patchApplication } = useUpdateApplicationDetails();
  const { mutateAsync: patchPerson } = usePatchPerson();
  const patchAppComment = usePatchAppComment();
  const { mutateAsync: deleteExpense } = useDeleteApplicationExpense();
  const { mutateAsync: patchExpense } = usePatchApplicationExpense();
  const { mutateAsync: createExpense } = useCreateApplicationExpense();
  const { mutateAsync: deleteCar } = useDeleteApplicationCar();
  const { mutateAsync: patchCar } = usePatchApplicationCar();
  const { mutateAsync: createCar } = useCreateApplicationCar();
  const { data: mainApplicantPersonData } = usePerson(
    latestApplicationData?.PersonID,
  );
  const { data: coApplicantPersonData } = usePerson(
    latestApplicationData?.CoApplicantPersonID,
  );

  const { data: appCommentData } = useGetAppComment(applicationID);

  const { selectedTab, isEditing, setIsEditing } =
    useApplicationDataStore(applicationID);
  const resolver = useApplicationDataFormResolver(applicationID);

  const isSecuredLending =
    latestApplicationData?.Product === Product.SecuredLoan;
  const { data: axoVersionData, isLoading: isLoadingAxoVersionData } =
    useAxoVersion(
      isSecuredLending && selectedTab.type === ApplicationDataTabType.ORIGINAL
        ? applicationID
        : undefined,
    );
  const { data: applicationRevisionData } = useLoanApplicationRevision({
    applicationID,
    revision: axoVersionData?.OriginalRevision,
  });

  const requestID =
    (selectedTab.type === ApplicationDataTabType.REQUEST &&
      selectedTab.requestID) ||
    (selectedTab.type === ApplicationDataTabType.DRAFT &&
      selectedTab.sourceRequestID) ||
    undefined;

  const { data: requestData } = useLoanRequest({
    loanApplicationID: applicationID,
    loanRequestID: requestID,
  });

  const revisionedApplicationData =
    isSecuredLending && !isLoadingAxoVersionData && axoVersionData
      ? applicationRevisionData
      : undefined;
  const applicationData = revisionedApplicationData ?? latestApplicationData;

  const values = useMemo(() => {
    return {
      CommentTitle: appCommentData?.Title ?? '',
      CommentBody: appCommentData?.Body ?? '',
      CommentCreatedBy: appCommentData?.CreatedBy ?? '',
      CommentCreatedOn: appCommentData?.UpdatedAt ?? '',
      Purpose: requestData?.Purpose ?? applicationData?.Purpose ?? null,
      OtherPurpose:
        requestData?.OtherPurpose ?? applicationData?.OtherPurpose ?? null,
      LoanDuration:
        requestData?.Duration ?? applicationData?.LoanDuration ?? null,
      AppliedAmount:
        requestData?.Amount ?? applicationData?.AppliedAmount ?? null,
      ApplicantMode:
        requestData?.ApplicantMode ?? ApplicantMode.ApplicantModeNormal,
      SelectedLenders: new Set(),
      MainBank: requestData?.MainBank ?? applicationData?.MainBank ?? null,
      Citizenship:
        requestData?.Citizenship ?? applicationData?.Citizenship ?? null,
      CoApplicantCitizenship:
        requestData?.CoApplicantCitizenship ??
        applicationData?.CoApplicantCitizenship ??
        null,
      LivedInMarketCountrySince:
        requestData?.LivedInMarketCountrySince ??
        applicationData?.LivedInMarketCountrySince ??
        null,
      CoApplicantLivedInMarketCountrySince:
        requestData?.CoApplicantLivedInMarketCountrySince ??
        applicationData?.CoApplicantLivedInMarketCountrySince ??
        null,
      Education: requestData?.Education ?? applicationData?.Education ?? null,
      CoApplicantEducation:
        requestData?.CoApplicantEducation ??
        applicationData?.CoApplicantEducation ??
        null,
      MilitaryServiceCompleted:
        requestData?.MilitaryServiceCompleted ??
        applicationData?.MilitaryServiceCompleted ??
        null,
      PoliticallyInfluential:
        requestData?.PoliticallyInfluential ??
        applicationData?.PoliticallyInfluential ??
        null,
      Employer: requestData?.Employer ?? applicationData?.Employer ?? null,
      CoApplicantEmployer:
        requestData?.CoApplicantEmployer ??
        applicationData?.CoApplicantEmployer ??
        null,
      EmployerIndustry:
        requestData?.EmployerIndustry ||
        applicationData?.EmployerIndustry ||
        null,
      EmploymentStatus:
        requestData?.EmploymentStatus ??
        applicationData?.EmploymentStatus ??
        null,
      CoApplicantEmploymentStatus:
        requestData?.CoApplicantEmploymentStatus ??
        applicationData?.CoApplicantEmploymentStatus ??
        null,
      EmploymentStatusSince:
        requestData?.EmploymentStatusSince ??
        applicationData?.EmploymentStatusSince ??
        null,
      CoApplicantEmploymentStatusSince:
        requestData?.CoApplicantEmploymentStatusSince ??
        applicationData?.CoApplicantEmploymentStatusSince ??
        null,
      EmploymentStatusUntil:
        requestData?.EmploymentStatusUntil ??
        applicationData?.EmploymentStatusUntil ??
        null,
      CoApplicantEmploymentStatusUntil:
        requestData?.CoApplicantEmploymentStatusUntil ??
        applicationData?.CoApplicantEmploymentStatusUntil ??
        null,
      JobTitle: requestData?.JobTitle ?? applicationData?.JobTitle ?? null,
      JobPosition:
        requestData?.JobPosition ?? applicationData?.JobPosition ?? null,
      GrossIncomeMonthly:
        requestData?.GrossIncomeMonthly ??
        applicationData?.GrossIncomeMonthly ??
        null,
      GrossIncomeYearly:
        requestData?.GrossIncomeYearly ??
        applicationData?.GrossIncomeYearly ??
        null,
      CoApplicantGrossIncomeYearly:
        requestData?.CoApplicantGrossIncomeYearly ??
        applicationData?.CoApplicantGrossIncomeYearly ??
        null,
      CoApplicantNetIncomeYearly:
        requestData?.CoApplicantNetIncomeYearly ??
        applicationData?.CoApplicantNetIncomeYearly ??
        null,
      NetIncomeMonthly:
        requestData?.NetIncomeMonthly ??
        applicationData?.NetIncomeMonthly ??
        null,
      ExtraIncomeMonthly:
        requestData?.ExtraIncomeMonthly ??
        applicationData?.ExtraIncomeMonthly ??
        null,
      ExtraIncomeRental:
        requestData?.ExtraIncomeRental ??
        applicationData?.ExtraIncomeRental ??
        null,
      ExtraIncomeChildSupport:
        requestData?.ExtraIncomeChildSupport ??
        applicationData?.ExtraIncomeChildSupport ??
        null,
      LivingCostMonthly:
        requestData?.LivingCostMonthly ??
        applicationData?.LivingCostMonthly ??
        null,
      CoApplicantLivingCostMonthly:
        requestData?.CoApplicantLivingCostMonthly ??
        applicationData?.CoApplicantLivingCostMonthly ??
        null,
      MaritalStatus:
        requestData?.MaritalStatus ?? applicationData?.MaritalStatus ?? null,
      CoApplicantMaritalStatus:
        requestData?.CoApplicantMaritalStatus ??
        applicationData?.CoApplicantMaritalStatus ??
        null,
      PartnerGrossIncomeMonthly:
        requestData?.PartnerGrossIncomeMonthly ??
        applicationData?.PartnerGrossIncomeMonthly ??
        null,
      PartnerGrossIncomeYearly:
        requestData?.PartnerGrossIncomeYearly ??
        applicationData?.PartnerGrossIncomeYearly ??
        null,
      PartnerLivingCostMonthly:
        requestData?.PartnerLivingCostMonthly ??
        applicationData?.PartnerLivingCostMonthly ??
        null,
      CoApplicantPartnerGrossIncomeYearly:
        requestData?.CoApplicantPartnerGrossIncomeYearly ??
        applicationData?.CoApplicantPartnerGrossIncomeYearly ??
        null,
      HousingType:
        requestData?.HousingType ?? applicationData?.HousingType ?? null,
      CoApplicantIsCohabitant:
        requestData?.CoApplicantIsCohabitant ??
        applicationData?.CoApplicantIsCohabitant ??
        null,
      CoApplicantHousingType:
        requestData?.CoApplicantHousingType ??
        applicationData?.CoApplicantHousingType ??
        null,
      ApartmentType:
        requestData?.ApartmentType ?? applicationData?.ApartmentType ?? null,
      LivedAtAddressSince: mainApplicantPersonData?.LivedAtAddressSince ?? null,
      CoApplicantLivedAtAddressSince:
        coApplicantPersonData?.LivedAtAddressSince ?? null,
      RentCostMonthly:
        requestData?.RentCostMonthly ??
        applicationData?.RentCostMonthly ??
        null,
      CoApplicantRentCostMonthly:
        requestData?.CoApplicantRentCostMonthly ??
        applicationData?.CoApplicantRentCostMonthly ??
        null,
      OwnerAssociationCostMonthly:
        requestData?.OwnerAssociationCostMonthly ??
        applicationData?.OwnerAssociationCostMonthly ??
        null,
      Children:
        (requestData?.Children ?? applicationData?.Children)?.map((child) => ({
          ID: child.ID ?? null,
          Age: child.Age ?? null,
        })) ?? [],
      Debts:
        (requestData?.Debts ?? applicationData?.Debts)
          ?.filter((debt) => debt.Type && debt.Debtor !== Debtor.CoApplicant)
          .map((item) => ({
            ID: item.ID ?? null,
            Type: item.Type ?? null,
            MonthlyDebtPayment: item.MonthlyDebtPayment ?? 0,
            RefinanceAmount: item.RefinanceAmount ?? 0,
            OutstandingAmount: item.OutstandingAmount ?? 0,
            Debtor: item.Debtor ?? null,
            Creditor: item.Creditor ?? null,
            Source: item.Source ?? null,
            NominalInterestRate: item.NominalInterestRate
              ? item.NominalInterestRate
              : null,
          })) ?? [],
      CoDebts:
        (requestData?.CoDebts ?? applicationData?.CoDebts)
          ?.filter((debt) => debt.Type && debt.Debtor !== Debtor.MainApplicant)
          .map((item) => ({
            ID: item.ID ?? null,
            Type: item.Type ?? null,
            MonthlyDebtPayment: item.MonthlyDebtPayment ?? 0,
            RefinanceAmount: item.RefinanceAmount ?? 0,
            OutstandingAmount: item.OutstandingAmount ?? 0,
            Debtor: item.Debtor ?? null,
            Creditor: item.Creditor ?? null,
            Source: item.Source ?? null,
            NominalInterestRate: item.NominalInterestRate
              ? item.NominalInterestRate
              : null,
          })) ?? [],
      ResidencePermit:
        requestData?.ResidencePermit ??
        applicationData?.ResidencePermit ??
        null,
      CoApplicantResidencePermit:
        requestData?.CoApplicantResidencePermit ??
        applicationData?.CoApplicantResidencePermit ??
        null,
      ResidencePermitType:
        requestData?.ResidencePermitType ??
        applicationData?.ResidencePermitType ??
        null,
      CoApplicantResidencePermitType:
        requestData?.CoApplicantResidencePermitType ??
        applicationData?.CoApplicantResidencePermitType ??
        null,
      ResidencePermitNumber:
        requestData?.ResidencePermitNumber ??
        applicationData?.ResidencePermitNumber ??
        null,
      CoApplicantResidencePermitNumber:
        requestData?.CoApplicantResidencePermitNumber ??
        applicationData?.CoApplicantResidencePermitNumber ??
        null,
      ResidencePermitExpiration:
        requestData?.ResidencePermitExpiration ??
        applicationData?.ResidencePermitExpiration ??
        null,
      CoApplicantResidencePermitExpiration:
        requestData?.CoApplicantResidencePermitExpiration ??
        applicationData?.CoApplicantResidencePermitExpiration ??
        null,
      CompanyOrganizationNumber:
        requestData?.CompanyOrganizationNumber ??
        applicationData?.CompanyOrganizationNumber ??
        null,
      CoApplicantCompanyOrganizationNumber:
        requestData?.CoApplicantCompanyOrganizationNumber ??
        applicationData?.CoApplicantCompanyOrganizationNumber ??
        null,
      OwnsHolidayHome:
        requestData?.OwnsHolidayHome ??
        applicationData?.OwnsHolidayHome ??
        null,
      CurrentYearDebtOutstandingAmount:
        requestData?.CurrentYearDebtOutstandingAmount ??
        applicationData?.CurrentYearDebtOutstandingAmount ??
        null,
      HasUnemploymentFund:
        requestData?.HasUnemploymentFund ??
        applicationData?.HasUnemploymentFund ??
        null,
      CoApplicantHasUnemploymentFund:
        requestData?.CoApplicantHasUnemploymentFund ??
        applicationData?.CoApplicantHasUnemploymentFund ??
        null,
      YearsAsBankCustomer:
        requestData?.YearsAsBankCustomer ??
        applicationData?.YearsAsBankCustomer ??
        null,
      HasDankort:
        requestData?.HasDankort ?? applicationData?.HasDankort ?? null,
      HasOtherPaymentCards:
        requestData?.HasOtherPaymentCards ??
        applicationData?.HasOtherPaymentCards ??
        null,
      HasCars: requestData?.HasCars ?? applicationData?.HasCars ?? null,
      CarOwnerShipType:
        (requestData?.Cars ?? applicationData?.Cars)?.find(Boolean)
          ?.OwnershipType ?? null,
      CarTotalCostMonthly:
        (requestData?.Cars ?? applicationData?.Cars)?.find(Boolean)
          ?.TotalCostMonthly ?? null,
      ExpensesPhoneInternetTV:
        (requestData?.Expenses ?? applicationData?.Expenses)?.find(
          (x) => x.Kind === ExpenseKind.TVPhoneInternet,
        )?.Value ?? null,
      ExpensesTransport:
        (requestData?.Expenses ?? applicationData?.Expenses)?.find(
          (x) => x.Kind === ExpenseKind.Transport,
        )?.Value ?? null,
      ExpensesElectricity:
        (requestData?.Expenses ?? applicationData?.Expenses)?.find(
          (x) => x.Kind === ExpenseKind.Electricity,
        )?.Value ?? null,
      ExpensesWater:
        (requestData?.Expenses ?? applicationData?.Expenses)?.find(
          (x) => x.Kind === ExpenseKind.Water,
        )?.Value ?? null,
      ExpensesHeating:
        (requestData?.Expenses ?? applicationData?.Expenses)?.find(
          (x) => x.Kind === ExpenseKind.Heating,
        )?.Value ?? null,
      ExpensesInsurances:
        (requestData?.Expenses ?? applicationData?.Expenses)?.find(
          (x) => x.Kind === ExpenseKind.Insurance,
        )?.Value ?? null,
      PartnerNetIncomeYearly:
        requestData?.PartnerNetIncomeYearly ??
        applicationData?.PartnerNetIncomeYearly ??
        null,
    } satisfies ApplicationDataFormValues;
  }, [
    applicationData,
    requestData,
    coApplicantPersonData,
    mainApplicantPersonData,
    appCommentData,
  ]);

  const createLoanRequestFromFormValues =
    useCreateLoanRequestFromFormValues(applicationID);
  const { mutateAsync: registerLoanRequests } =
    useRegisterLoanRequests(applicationID);

  const formMethods = useForm<ApplicationDataFormValues>({
    defaultValues: values,
    values,
    resolver,
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  const { trigger, reset, control } = formMethods;

  useEffect(() => {
    trigger();
  }, [trigger, values, isEditing]);

  const onSubmit = useCallback(
    async (values: ApplicationDataFormValues) => {
      const applicationDataCopy = structuredClone(applicationData);

      if (
        applicationDataCopy?.PersonID &&
        control.getFieldState('LivedAtAddressSince').isDirty
      ) {
        await patchPerson({
          id: applicationDataCopy?.PersonID,
          data: {
            LivedAtAddressSince: values.LivedAtAddressSince ?? undefined,
          },
        });
      }

      if (
        applicationDataCopy?.CoApplicantPersonID &&
        control.getFieldState('CoApplicantLivedAtAddressSince').isDirty
      ) {
        await patchPerson({
          id: applicationDataCopy?.CoApplicantPersonID,
          data: {
            LivedAtAddressSince:
              values.CoApplicantLivedAtAddressSince ?? undefined,
          },
        });
      }

      if (applicationDataCopy?.Product === Product.SecuredLoan) {
        const patchData = {
          Title: values.CommentTitle ?? '',
          Body: values.CommentBody ?? '',
        };
        await patchAppComment.mutateAsync({
          applicationID,
          patchData,
        });
      }

      if (
        selectedTab.type === ApplicationDataTabType.DRAFT ||
        (selectedTab.type === ApplicationDataTabType.AXO_VERSION &&
          selectedTab.currentPage === ApplicationDataTabPage.LENDERS) ||
        (selectedTab.type === ApplicationDataTabType.ORIGINAL &&
          selectedTab.currentPage === ApplicationDataTabPage.LENDERS)
      ) {
        await registerLoanRequests({
          loanRequests: Array.from(values.SelectedLenders)
            .map((lenderID) =>
              createLoanRequestFromFormValues(lenderID, values),
            )
            .filter((request) => request !== undefined),
        });
      } else {
        await log(EventCodeACE.UpdateApplicationData);
        let status = applicationDataCopy?.Status ?? null;

        if (status === ApplicationStatus.ApplicationStatusValidationFailed) {
          // Force a data revalidation on this scenario
          status =
            ApplicationStatus.ApplicationStatusPendingReceiptCustomerInputComplete;
        }

        await patchApplication({
          applicationID,
          patch: {
            Status: status,

            LoanDuration: values.LoanDuration,
            Purpose: values.Purpose,
            OtherPurpose: values.OtherPurpose,
            AppliedAmount: values.AppliedAmount,

            MainBank: values.MainBank,
            Citizenship: values.Citizenship,
            LivedInMarketCountrySince: values.LivedInMarketCountrySince,
            Education: values.Education,
            MilitaryServiceCompleted: values.MilitaryServiceCompleted,
            PoliticallyInfluential: values.PoliticallyInfluential,
            EmploymentStatus: values.EmploymentStatus,
            Employer: values.Employer,
            EmployerIndustry: values.EmployerIndustry,
            EmploymentStatusSince: values.EmploymentStatusSince,
            EmploymentStatusUntil: values.EmploymentStatusUntil,
            JobTitle: values.JobTitle,
            JobPosition: values.JobPosition,
            GrossIncomeMonthly: values.GrossIncomeMonthly,
            GrossIncomeYearly: values.GrossIncomeYearly,
            NetIncomeMonthly: values.NetIncomeMonthly,
            ExtraIncomeMonthly: values.ExtraIncomeMonthly,
            ExtraIncomeRental: values.ExtraIncomeRental,
            ExtraIncomeChildSupport: values.ExtraIncomeChildSupport,
            LivingCostMonthly: values.LivingCostMonthly,
            MaritalStatus: values.MaritalStatus,
            PartnerGrossIncomeMonthly: values.PartnerGrossIncomeMonthly,
            PartnerGrossIncomeYearly: values.PartnerGrossIncomeYearly,
            PartnerNetIncomeYearly: values.PartnerNetIncomeYearly,
            PartnerLivingCostMonthly: values.PartnerLivingCostMonthly,
            HousingType: values.HousingType,
            ApartmentType: values.ApartmentType,
            RentCostMonthly: values.RentCostMonthly,
            OwnerAssociationCostMonthly: values.OwnerAssociationCostMonthly,
            // eslint-disable-next-line @eslint-react/no-children-map -- False positive
            Children: values.Children.map((child) => ({
              ID: (child.ID ?? undefined) as never,
              Age: child.Age ?? 0,
              LoanApplicationID: applicationID,
            })),
            Debts: values.Debts.filter((debt) => Boolean(debt.Type)).map(
              (debt) => ({
                ID: (debt.ID ?? undefined) as never,
                Type: debt.Type as never,
                Creditor: debt.Creditor ?? undefined,
                MonthlyDebtPayment: debt.MonthlyDebtPayment ?? undefined,
                NominalInterestRate: debt.NominalInterestRate ?? undefined,
                OutstandingAmount: debt.OutstandingAmount,
                RefinanceAmount: debt.RefinanceAmount ?? undefined,
                Source: debt.Source ?? undefined,
                Debtor: Debtor.MainApplicant,
                LoanApplicationID: applicationID,
              }),
            ),
            CoApplicantCitizenship: values.CoApplicantCitizenship,
            CoApplicantLivedInMarketCountrySince:
              values.CoApplicantLivedInMarketCountrySince,
            CoApplicantEducation: values.CoApplicantEducation,
            CoApplicantEmployer: values.CoApplicantEmployer,
            CoApplicantEmploymentStatus: values.CoApplicantEmploymentStatus,
            CoApplicantEmploymentStatusSince:
              values.CoApplicantEmploymentStatusSince,
            CoApplicantEmploymentStatusUntil:
              values.CoApplicantEmploymentStatusUntil,
            CoApplicantGrossIncomeYearly: values.CoApplicantGrossIncomeYearly,
            CoApplicantNetIncomeYearly: values.CoApplicantNetIncomeYearly,
            CoApplicantLivingCostMonthly: values.CoApplicantLivingCostMonthly,
            CoApplicantMaritalStatus: values.CoApplicantMaritalStatus,
            CoApplicantPartnerGrossIncomeYearly:
              values.CoApplicantPartnerGrossIncomeYearly,
            CoApplicantIsCohabitant: values.CoApplicantIsCohabitant,
            CoApplicantHousingType: values.CoApplicantHousingType,
            CoApplicantRentCostMonthly: values.CoApplicantRentCostMonthly,
            CoDebts: values.CoDebts.filter((debt) => Boolean(debt.Type)).map(
              (debt) => ({
                ID: (debt.ID ?? undefined) as never,
                Type: debt.Type as never,
                Creditor: debt.Creditor ?? undefined,
                MonthlyDebtPayment: debt.MonthlyDebtPayment ?? undefined,
                NominalInterestRate: debt.NominalInterestRate ?? undefined,
                OutstandingAmount: debt.OutstandingAmount,
                RefinanceAmount: debt.RefinanceAmount ?? undefined,
                Source: debt.Source ?? undefined,
                Debtor: Debtor.CoApplicant,
                LoanApplicationID: applicationID,
              }),
            ),
            ResidencePermit: values.ResidencePermit,
            ResidencePermitType: values.ResidencePermitType,
            ResidencePermitNumber: values.ResidencePermitNumber,
            ResidencePermitExpiration: values.ResidencePermitExpiration,
            CoApplicantResidencePermit: values.CoApplicantResidencePermit,
            CoApplicantResidencePermitType:
              values.CoApplicantResidencePermitType,
            CoApplicantResidencePermitNumber:
              values.CoApplicantResidencePermitNumber,
            CoApplicantResidencePermitExpiration:
              values.CoApplicantResidencePermitExpiration,
            CompanyOrganizationNumber: values.CompanyOrganizationNumber,
            CoApplicantCompanyOrganizationNumber:
              values.CoApplicantCompanyOrganizationNumber,
            HasUnemploymentFund: values.HasUnemploymentFund,
            CoApplicantHasUnemploymentFund:
              values.CoApplicantHasUnemploymentFund,
            YearsAsBankCustomer: values.YearsAsBankCustomer,
            HasDankort: values.HasDankort,
            HasOtherPaymentCards: values.HasOtherPaymentCards,
            HasCars: values.HasCars,
            OwnsHolidayHome: values.OwnsHolidayHome,
            CurrentYearDebtOutstandingAmount:
              values.CurrentYearDebtOutstandingAmount,
          },
        });

        const expenses: Array<{
          Kind: ExpenseKind;
          Value: number | null;
        }> = [
          {
            Kind: ExpenseKind.TVPhoneInternet,
            Value: values.ExpensesPhoneInternetTV,
          },
          {
            Kind: ExpenseKind.Transport,
            Value: values.ExpensesTransport,
          },
          {
            Kind: ExpenseKind.Electricity,
            Value: values.ExpensesElectricity,
          },
          {
            Kind: ExpenseKind.Water,
            Value: values.ExpensesWater,
          },
          {
            Kind: ExpenseKind.Heating,
            Value: values.ExpensesHeating,
          },
          {
            Kind: ExpenseKind.Insurance,
            Value: values.ExpensesInsurances,
          },
        ];

        await Promise.all(
          expenses.map(async (expense) => {
            const expenseID =
              applicationDataCopy?.Expenses?.find(
                (y) => y.Kind === expense.Kind,
              )?.ID ?? null;

            const previousValue =
              applicationDataCopy?.Expenses?.find(
                (y) => y.Kind === expense.Kind,
              )?.Value ?? null;

            if ((expense.Value ?? 0) === (previousValue ?? 0)) return;

            if (expense.Value) {
              if (expenseID) {
                await patchExpense({
                  applicationID,
                  expenseID,
                  data: {
                    Kind: expense.Kind,
                    Value: expense.Value,
                  },
                });
              } else {
                await createExpense({
                  applicationID,
                  data: {
                    Kind: expense.Kind,
                    Value: expense.Value,
                  },
                });
              }
            } else if (expenseID) {
              await deleteExpense({
                applicationID,
                expenseID,
              });
            }
          }),
        );

        if (values.HasCars) {
          const carID =
            applicationDataCopy?.Cars?.find((x) => x.ID)?.ID ?? null;
          if (carID) {
            await patchCar({
              applicationID,
              carID,
              data: {
                OwnershipType: values.CarOwnerShipType as CarOwnerShipType,
                TotalCostMonthly: values.CarTotalCostMonthly ?? 0,
              },
            });
          } else {
            await createCar({
              applicationID,
              data: {
                OwnershipType: values.CarOwnerShipType as CarOwnerShipType,
                TotalCostMonthly: values.CarTotalCostMonthly ?? 0,
              },
            });
          }
        } else {
          await Promise.all(
            applicationDataCopy?.Cars?.map(async (car) => {
              await deleteCar({ applicationID, carID: car.ID });
            }) ?? [],
          );
        }
      }

      setIsEditing(false);
      reset();
      trigger();
    },
    [
      applicationData,
      patchAppComment,
      selectedTab,
      applicationID,
      createLoanRequestFromFormValues,
      patchApplication,
      patchPerson,
      registerLoanRequests,
      setIsEditing,
      log,
      reset,
      trigger,
      deleteExpense,
      createExpense,
      patchExpense,
      deleteCar,
      createCar,
      patchCar,
      control,
    ],
  );

  return (
    <form
      onSubmit={formMethods.handleSubmit(onSubmit, () =>
        onSubmit(formMethods.getValues()),
      )}
      style={{
        display: 'contents',
      }}
    >
      <FormProvider {...formMethods}>{children}</FormProvider>
    </form>
  );
};
